import React from 'react';
import Product from '../components/Product';
import OrderBumps, { useHasOrderBumps } from '../components/CheckoutForm/components/OrderBumps';
import Variants from '../components/CheckoutForm/components/Variants';
import CheckoutForm from '../components/CheckoutForm';
import useCheckoutState from '../hooks/useCheckoutState';
import useAiTarget from '../hooks/useAiTarget';
import {
  checkHasEventModules,
  checkHasEventWrapperContent,
  checkHasProductModules,
  checkHasProductWrapperContent,
} from '../enums/modules';
import useCartCost from '../hooks/useCartCost';
import useEffectivePricing from '../hooks/useEffectivePricing';
import clsx from 'clsx';
import CheckoutBrandingBadge from '../components/CheckoutBrandingBadge';
import CheckoutConfirmation from '../CheckoutConfirmation';
import CheckoutFormContainer from '../components/CheckoutFormContainer';
import { LogoBanner } from '../components/Product/Product';
import { PAGE_TYPES } from '../enums/page-types';
import EventDetails from '../components/EventDetails';
import TicketGroups from '../components/TicketGroups';
import checkHasVisibleVariants from '../lib/checkHasVisibleVariants';
import getUrlFromCdn, { CDNFitOption } from '../services/getUrlFromCdn';
import StyledElement from '@repo/checkout/components/StyledElement';
import ProductPricePicker from '../components/Product/ProductPricePicker';
import { isMultiPrice } from '../lib/multiPrice';
import DocumentBackground from '../components/DocumentBackground';

export default function CheckoutSplit() {
  const {
    theme,
    checkoutType,
    checkoutLayout,
    checkout,
    product,
    seller,
    query,
    form,
    isPreview,
    isScreenshot,
    isConfirmation,
    isTestingPayments,
    isUpsell,
  } = useCheckoutState();
  const aiTarget = useAiTarget();
  const { productPriceAmount, setupFeeAmount } = useCartCost();
  const { effectiveDiscountedFromPrice, effectiveTrialPeriodDays, effectiveStartDate } =
    useEffectivePricing();

  const hasVisibleVariants = checkHasVisibleVariants(
    product.variants,
    form.variants,
    query,
    isPreview,
  );

  // Unset position stays 'content' here: Split historically rendered these in
  // the content section (Double's unset default is 'form').
  const variantsPosition = theme?.elements?.checkout?.variants?.position || 'content';
  const ticketGroupsPosition = theme?.elements?.checkout?.ticketGroups?.position || 'content';
  const pricesPosition = theme?.elements?.product?.pricePicker?.position || 'content';

  const shouldRenderVariantsInContent = hasVisibleVariants && variantsPosition === 'content';
  const shouldRenderVariantsInForm = hasVisibleVariants && variantsPosition === 'form';

  // Bumps sit under the variants and follow them between columns by default,
  // but unlike variants they are not gated on the product having any: a page
  // with bumps and no variants still has to show them.
  const orderBumpsPosition =
    theme?.elements?.checkout?.form?.orderBumps?.position || variantsPosition;
  const hasOrderBumps = useHasOrderBumps();
  const shouldRenderBumpsInContent = hasOrderBumps && orderBumpsPosition === 'content';
  const shouldRenderBumpsInForm = hasOrderBumps && orderBumpsPosition === 'form';
  const shouldRenderPricesInForm =
    pricesPosition === 'form' &&
    theme?.elements?.product?.pricePicker?.enabled !== false &&
    isMultiPrice(product?.prices, query);

  const hasProductModules = checkHasProductModules({
    theme,
    checkout,
    seller,
    query,
    isScreenshot,
    productPriceAmount,
    setupFeeAmount,
    discountedFromPrice: effectiveDiscountedFromPrice,
    trialPeriodDays: effectiveTrialPeriodDays,
    startDate: effectiveStartDate,
    checkoutType,
    checkoutLayout,
  });

  const hasEventModules = checkHasEventModules({ theme, checkout, seller });
  const hasTicketTypes = checkout.type === PAGE_TYPES.EVENT;
  const shouldRenderTicketsInContent = hasTicketTypes && ticketGroupsPosition === 'content';
  const shouldRenderTicketsInForm = hasTicketTypes && ticketGroupsPosition === 'form';

  const hasProductWrapperContent = checkHasProductWrapperContent({
    theme,
    checkout,
    seller,
    query,
    isScreenshot,
    productPriceAmount,
    setupFeeAmount,
    discountedFromPrice: effectiveDiscountedFromPrice,
    trialPeriodDays: effectiveTrialPeriodDays,
    startDate: effectiveStartDate,
    checkoutType,
    checkoutLayout,
  });

  const hasEventWrapperContent = checkHasEventWrapperContent({
    theme,
    checkout,
    seller,
    checkoutType,
  });

  // Read mainColumn from theme with fallback to checkout.formPosition
  const mainColumn = theme?.layout?.splitLayout?.mainColumn || checkout.formPosition;

  const defaultWidth = 1920;
  const defaultHeight = 1080;

  const useAbsolutePositioning = isPreview && !isTestingPayments;

  const hasContentSection =
    hasProductModules ||
    hasEventModules ||
    shouldRenderTicketsInContent ||
    shouldRenderVariantsInContent;

  const hasFormBlocks =
    shouldRenderPricesInForm ||
    shouldRenderTicketsInForm ||
    shouldRenderVariantsInForm ||
    shouldRenderBumpsInForm;

  const dividerEnabled = theme.elements?.checkout?.divider?.enabled !== false;

  // Mirrors CheckoutCenter: when the last content item has nothing besides
  // media (no title/price/description/etc.), Product/EventDetails cancel
  // their own pb-4 — the form needs pt-4 to avoid sitting flush against
  // the image even when the divider is hidden. Split layout doesn't run
  // that flush (gated on isCenterLayout), so this is always false here,
  // but kept for parity in case the flush ever extends to split.
  const lastContentItemFlushed =
    hasContentSection &&
    !shouldRenderVariantsInContent &&
    !shouldRenderTicketsInContent &&
    (hasEventModules
      ? !hasEventWrapperContent
      : hasProductModules
        ? !hasProductWrapperContent
        : false);

  // A colored content panel needs separation from the form even when the
  // divider is hidden. Default (both unset) means both resolve to the same
  // token, so no extra spacing is needed.
  const splitLayout = theme.layout?.splitLayout;
  const contentBgOverride = splitLayout?.contentBackgroundColor;
  const formBgOverride = splitLayout?.formBackgroundColor;
  const contentBgDiffersFromForm =
    (!!contentBgOverride || !!formBgOverride) &&
    (contentBgOverride?.light !== formBgOverride?.light ||
      contentBgOverride?.dark !== formBgOverride?.dark);

  const formNeedsTopPadding =
    !hasContentSection || dividerEnabled || lastContentItemFlushed || contentBgDiffersFromForm;

  return (
    <div className="cp_checkout-split-container relative min-h-full overflow-x-hidden">
      <DocumentBackground
        color={
          hasContentSection && !isConfirmation
            ? 'var(--cp_content-background-color)'
            : 'var(--cp_form-background-color)'
        }
      />
      {!useAbsolutePositioning &&
        (theme.layout?.splitLayout?.backgroundImage?.url ? (
          <div
            className="top-0 bottom-0 w-1/2 hidden @xl/checkout:block pointer-events-none bg-[var(--cp_background-color)] fixed"
            style={{
              left: mainColumn === 'left' ? '50%' : '0%',
              backgroundImage: `image-set(url("${getUrlFromCdn(
                theme.layout.splitLayout.backgroundImage.url,
                {
                  width: defaultWidth,
                  height: defaultHeight,
                  fit: CDNFitOption.cover,
                },
              )}"), url("${getUrlFromCdn(theme.layout.splitLayout.backgroundImage.url, {
                width: defaultWidth * 2,
                height: defaultHeight * 2,
                fit: CDNFitOption.cover,
              })}") 2x)`,
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: `var(--cp_background-position)`,
            }}
          />
        ) : (
          <div
            className="top-0 bottom-0 w-1/2 hidden @xl/checkout:block pointer-events-none bg-[var(--cp_background-color)] fixed"
            style={{
              left: mainColumn === 'left' ? '50%' : '0%',
            }}
          />
        ))}

      <div
        className={clsx(
          'fixed h-screen w-screen inset-0 bg-fixed bg-no-repeat bg-cover bg-center -z-[1]',
          'hidden',
          'bg-[var(--cp_background-color)]',
        )}
      />

      <div className={clsx(useAbsolutePositioning ? 'w-full' : 'mx-auto max-w-screen-xl')}>
        <div
          {...(isUpsell && aiTarget('upsell'))}
          className={clsx(
            'cp_checkout cp_checkout-split min-h-screen grid grid-cols-1 @xl/checkout:grid-cols-2',
            {
              'px-0 my-0': checkoutType !== 'embed',
            },
          )}
        >
          {/* Decorative spacer column - in preview, provides background directly */}
          <div
            className={clsx('hidden @xl/checkout:block relative z-0', {
              'order-2': mainColumn === 'left',
              'order-1': mainColumn === 'right',
              'bg-[var(--cp_background-color)]': useAbsolutePositioning,
            })}
            style={
              useAbsolutePositioning && theme.layout?.splitLayout?.backgroundImage?.url
                ? {
                    backgroundImage: `image-set(url("${getUrlFromCdn(
                      theme.layout.splitLayout.backgroundImage.url,
                      {
                        width: defaultWidth,
                        height: defaultHeight,
                        fit: CDNFitOption.cover,
                      },
                    )}"), url("${getUrlFromCdn(theme.layout.splitLayout.backgroundImage.url, {
                      width: defaultWidth * 2,
                      height: defaultHeight * 2,
                      fit: CDNFitOption.cover,
                    })}") 2x)`,
                    backgroundSize: 'cover',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: `var(--cp_background-position)`,
                  }
                : {}
            }
          />

          {/* Content column - in preview, uses flex so form section can grow */}
          <div
            className={clsx('flex flex-col z-[1]', {
              'order-1': mainColumn === 'left',
              'order-2': mainColumn === 'right',
              'flex flex-col': useAbsolutePositioning,
              'shadow-[var(--cp_checkout-form-background-shadow-right)]': mainColumn === 'left',
              'shadow-[var(--cp_checkout-form-background-shadow-left)]': mainColumn === 'right',
            })}
          >
            <LogoBanner className="@xl/checkout:max-w-[calc(50%-2rem)]" />

            {isConfirmation && (
              <div
                className={clsx(
                  'relative bg-[var(--cp_form-background-color)]',
                  useAbsolutePositioning ? 'grow' : 'bg-transparent grow',
                )}
              >
                <div
                  className={clsx(
                    'block absolute inset-y-0 bg-[var(--cp_form-background-color)]',
                    mainColumn === 'right' ? 'left-0 -right-[100vw]' : 'right-0 -left-[100vw]',
                  )}
                />
                <div
                  className={clsx(
                    'cp_card mx-auto w-full relative z-10 rounded max-w-screen-xs',
                    'pb-12 pt-20 shadow-none',
                  )}
                >
                  <CheckoutConfirmation />
                </div>
              </div>
            )}

            {!isConfirmation && (
              <CheckoutFormContainer>
                {hasContentSection && (
                  <div
                    className={clsx(
                      'relative bg-[var(--cp_content-background-color)]',
                      !useAbsolutePositioning && 'bg-transparent',
                    )}
                  >
                    {!useAbsolutePositioning && (
                      <div
                        className={clsx(
                          'block absolute inset-y-0 bg-[var(--cp_content-background-color)]',
                          mainColumn === 'right' ? 'left-0 -right-[50vw]' : 'right-0 -left-[50vw]',
                        )}
                      />
                    )}
                    <div
                      className={clsx(
                        'cp_card mx-auto w-full relative z-10 rounded-t max-w-screen-xs',
                        'mx-auto pt-12 pt-20 shadow-none',
                      )}
                    >
                      {hasProductModules && (
                        <div className="px-4 pb-4">
                          <Product />
                        </div>
                      )}

                      {hasEventModules && (
                        <div className="px-4 pb-4">
                          <EventDetails />
                        </div>
                      )}

                      {shouldRenderTicketsInContent && (
                        <div className={clsx('cp_ticket-types-wrapper pb-4')}>
                          <TicketGroups />
                        </div>
                      )}

                      {shouldRenderVariantsInContent && (
                        <div className={clsx('cp_variants-wrapper pb-4')}>
                          <Variants />
                        </div>
                      )}

                      {shouldRenderBumpsInContent && (
                        <div className="cp_order-bumps-wrapper px-4 pb-4">
                          <OrderBumps />
                        </div>
                      )}
                    </div>
                    <StyledElement
                      elementPath="checkout.divider"
                      className="cp_divider z-10 border-b border-[var(--cp_checkout-divider-border-color)] hidden"
                    />
                    <StyledElement
                      elementPath="checkout.divider"
                      className={clsx(
                        'cp_divider z-10 border-b border-[var(--cp_checkout-divider-border-color)]',
                        'block absolute bottom-0',
                        mainColumn === 'right' ? 'left-0 -right-[50vw]' : 'right-0 -left-[50vw]',
                      )}
                    />
                  </div>
                )}

                <div
                  className={clsx(
                    'relative bg-[var(--cp_form-background-color)]',
                    useAbsolutePositioning ? 'grow' : 'bg-transparent grow',
                  )}
                  style={{ '--cp_branding-badge-color': 'var(--cp_branding-badge-color-on-form)' }}
                >
                  {!useAbsolutePositioning && (
                    <div
                      className={clsx(
                        'block absolute inset-y-0 bg-[var(--cp_form-background-color)]',
                        mainColumn === 'right' ? 'left-0 -right-[50vw]' : 'right-0 -left-[50vw]',
                      )}
                    />
                  )}
                  <div
                    className={clsx(
                      'cp_card mx-auto w-full relative z-10 max-w-screen-xs',
                      hasContentSection ? 'rounded-b' : 'rounded',
                      'mx-auto pb-12 max-w-screen-xs shadow-none',
                      !hasContentSection && 'pt-12 pt-20',
                      formNeedsTopPadding && 'pt-4',
                    )}
                  >
                    {hasFormBlocks && (
                      <>
                        {shouldRenderPricesInForm && <ProductPricePicker className="px-4 pb-4" />}

                        {shouldRenderTicketsInForm && (
                          <div className="cp_ticket-types-wrapper pb-4">
                            <TicketGroups />
                          </div>
                        )}

                        {shouldRenderVariantsInForm && (
                          <div className="cp_variants-wrapper pb-4">
                            <Variants />
                          </div>
                        )}

                        {shouldRenderBumpsInForm && (
                          <div className="cp_order-bumps-wrapper px-4 pb-4">
                            <OrderBumps />
                          </div>
                        )}
                      </>
                    )}

                    {/* Form blocks group with the fields: no divider between
                        them, and their pb-4 already provides the gap. */}
                    <div className="px-4 mb-4">
                      <CheckoutForm />
                    </div>

                    <CheckoutBrandingBadge />
                  </div>
                </div>
              </CheckoutFormContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
